import { TranslationsContext } from '@teliaee/telia-translate';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Heading, HorizontalProgress, HorizontalProgressItem, Separator } from '@teliaee/design-system';

import { useStores } from '../stores/RootStore';

import PaymentMethods from './PaymentMethods';
import PrepaidDataComponent from './PrepaidDataComponent';
import PriceSelector from './PriceSelector';
import FormErrorNotice from './FormErrorNotice';
import { getEmailFieldHelper, getPersonalCodeFieldHelper, getPhoneFieldHelper } from './../utils/FieldHelperUtil';
import { PrepaymentSteps } from './../Constants';

const PrePaymentComponent = observer(() => {
  const { prepaidDataStore, mobileNumberStore, emailStore, populationRegistryPersonStore, priceStore, routeStore } = useStores();
  const translate = React.useContext(TranslationsContext).translate;
  const showHeading = React.useMemo(() => routeStore.queryParam('headless') !== 'true', [routeStore]);
  const progressItemHeadingLevel = showHeading ? 2 : 3;
  const [currentStepIndex, setCurrentStepIndex] = React.useState(PrepaymentSteps.PREPAID_DATA_STEP);

  const getErroredFields = () => {
    const erroredFields = [];

    const phoneField = getPhoneFieldHelper(!mobileNumberStore.numberFieldInvalid, priceStore.noPlans, translate);
    if (phoneField) {
      erroredFields.push(phoneField);
    }

    const isEmailValid = !emailStore.orderInvoice || (emailStore.orderInvoice && !emailStore.emailFieldInvalid);
    const emailField = getEmailFieldHelper(isEmailValid, translate);
    if (emailField) {
      erroredFields.push(emailField);
    }

    const personalCodeField = getPersonalCodeFieldHelper(!populationRegistryPersonStore.personalCodeFieldInvalid, translate);
    if (personalCodeField) {
      erroredFields.push(personalCodeField);
    }

    return erroredFields;
  };

  const getStepState = (index: number): 'done' | 'current' | 'disabled' => {
    if (index < currentStepIndex) {
      return 'done';
    } else if (index === currentStepIndex) {
      return 'current';
    } else {
      return 'disabled';
    }
  };

  const isStepDisabled = (index: number) => {
    return ['disabled', 'done'].includes(getStepState(index));
  };

  return (
    <>
      {prepaidDataStore.isErrorNotificationVisible && (
        <>
          <FormErrorNotice erroredFields={getErroredFields()} />
          <Separator spacing="xxsmall" color="invisible" />
        </>
      )}
      {showHeading && (
        <Heading element="h1" className="h3">
          {translate('prepaid.title')}
        </Heading>
      )}
      <Separator spacing="xxsmall" color="invisible" />
      <HorizontalProgress break="md">
        <HorizontalProgressItem
          headingLevel={progressItemHeadingLevel}
          title={translate('prepaid.step.number.title')}
          state={getStepState(PrepaymentSteps.PREPAID_DATA_STEP)}
        >
          <PrepaidDataComponent setStepIndex={setCurrentStepIndex} disabled={isStepDisabled(PrepaymentSteps.PREPAID_DATA_STEP)} />
        </HorizontalProgressItem>
        <HorizontalProgressItem
          headingLevel={progressItemHeadingLevel}
          title={translate('prepaid.step.price.title')}
          state={getStepState(PrepaymentSteps.PRICE_STEP)}
        >
          <PriceSelector setStepIndex={setCurrentStepIndex} disabled={isStepDisabled(PrepaymentSteps.PRICE_STEP)} />
        </HorizontalProgressItem>
        <HorizontalProgressItem
          headingLevel={progressItemHeadingLevel}
          title={translate('prepaid.step.bank.title')}
          state={getStepState(PrepaymentSteps.PAYMENT_STEP)}
        >
          <PaymentMethods setStepIndex={setCurrentStepIndex} disabled={isStepDisabled(PrepaymentSteps.PAYMENT_STEP)} />
        </HorizontalProgressItem>
      </HorizontalProgress>
      <Separator color="invisible" spacing="xxsmall" />
    </>
  );
});

export default PrePaymentComponent;
